import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { processFlowsAPI } from '../../services/api';
import { generateNativePdf, printStyles, CompanyLetterhead } from './shared';

export function ProcessFlowPrintView() {
  const { id } = useParams();
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await processFlowsAPI.get(id);
        setFlow(res.data);
        setLoading(false);
        // Auto-trigger print after data loads
        setTimeout(() => {
          window.print();
        }, 500);
      } catch (err) {
        setError('Failed to load process flow');
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <style>{printStyles}</style>
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !flow) {
    return (
      <div className="print-container">
        <style>{printStyles}</style>
        <p style={{ color: 'red', textAlign: 'center' }}>{error || 'Process flow not found'}</p>
      </div>
    );
  }

  return (
    <div className="print-container">
      <style>{printStyles}</style>
      
      {/* Confidential Banner */}
      <div className="confidential-banner">
        ⚠️ CONFIDENTIAL - INTERNAL USE ONLY - DO NOT DISTRIBUTE ⚠️
      </div>
      
      {/* Header */}
      <div className="print-header">
        <div>
          <h1 className="company-name">SINGHANIA INTERNATIONAL LIMITED</h1>
          <p className="company-subtitle">Process Flow Document</p>
        </div>
        <div className="document-title">
          <h2>PROCESS FLOW</h2>
          <p className="document-number">Version {flow.version_number}</p>
        </div>
      </div>

      {/* Part Info */}
      <div className="info-grid">
        <div className="info-box">
          <h4>Part Details</h4>
          <p><span className="value">{flow.part_internal_no}</span></p>
          <p>{flow.part_description || 'No description'}</p>
          <p>Net Weight: {flow.net_weight_grams || '-'} grams</p>
        </div>
        <div className="info-box">
          <h4>Document Info</h4>
          <p>Created: <span className="value">{formatDate(flow.created_at)}</span></p>
          <p>Status: <span className="value">{flow.is_active ? 'Active' : 'Inactive'}</span></p>
          <p>Total Steps: <span className="value">{(flow.steps || []).length}</span></p>
        </div>
      </div>

      {/* Process Steps - NO COST INFORMATION */}
      <h3 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', color: '#1e40af' }}>
        Manufacturing Process Steps
      </h3>
      
      {(flow.steps || []).map((step, idx) => (
        <div key={step.process_name || `step-${idx}`} className="process-step">
          <div className="process-step-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div className="step-number">{idx + 1}</div>
              <span className="step-name">{step.process_name}</span>
            </div>
            <span className={`step-badge ${step.source === 'outsourced' ? 'badge-outsourced' : 'badge-inhouse'}`}>
              {step.source === 'outsourced' ? 'Outsourced' : 'In-House'}
            </span>
          </div>
          
          <div className="step-details">
            {step.machine_name && (
              <div className="step-detail">
                <label>Machine</label>
                <span>{step.machine_name}</span>
              </div>
            )}
            {step.source === 'outsourced' && step.vendor_name && (
              <div className="step-detail">
                <label>Vendor</label>
                <span>{step.vendor_name}</span>
              </div>
            )}
            {step.output_per_hour && (
              <div className="step-detail">
                <label>Output Rate</label>
                <span>{step.output_per_hour} pcs/min</span>
              </div>
            )}
            {step.oee_percentage && (
              <div className="step-detail">
                <label>OEE</label>
                <span>{step.oee_percentage}%</span>
              </div>
            )}
            {step.setup_time_minutes && (
              <div className="step-detail">
                <label>Setup Time</label>
                <span>{step.setup_time_minutes} min</span>
              </div>
            )}
            {step.tool_life_pieces && (
              <div className="step-detail">
                <label>Tool Life</label>
                <span>{step.tool_life_pieces.toLocaleString()} pcs</span>
              </div>
            )}
          </div>
          
          {step.remarks && (
            <p style={{ fontSize: '11px', color: '#64748b', marginTop: '8px', fontStyle: 'italic' }}>
              Note: {step.remarks}
            </p>
          )}
        </div>
      ))}

      {/* Footer */}
      <div className="footer">
        <p style={{ color: '#dc2626', fontWeight: 'bold' }}>
          CONFIDENTIAL: This document contains proprietary manufacturing information.
        </p>
        <p>Generated on {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
      </div>
    </div>
  );
}


// Costing Print View - Full Cost Breakdown with Cost/Kg columns

